import React from 'react';
import { ArrowRightIcon, CheckIcon } from 'lucide-react';
import { SmartLink } from './SmartLink';
import { SalesMock } from './mocks/SalesMock';
import { InventoryMock } from './mocks/InventoryMock';
import { ExpensesMock } from './mocks/ExpensesMock';
import { ReportsMock } from './mocks/ReportsMock';
import { FinanceMock } from './mocks/FinanceMock';
type Feature = {
  title: string;
  copy: string;
  cta: string;
  href?: string;
  bullets: string[];
  image: string;
  imageAlt: string;
  /** Light colour correction for uploaded photos that look flat or soft. */
  enhance?: boolean;
  /** Focal point for the crop, so the person stays in frame at every width. */
  imagePosition?: string;
  visual: React.ReactNode;
};
const features: Feature[] = [{
  title: 'Sell with confidence.',
  copy: 'Record sales, issue receipts, and see what’s selling across your business.',
  cta: 'Explore Point of Sale',
  href: '/products/sales-pos',
  bullets: ['Ring up sales in seconds on phone, tablet, or computer', 'Accept cash, mobile money, and card payments', 'Send digital or printed receipts to every customer', 'See best-selling products and daily totals by location'],
  image: 'https://cdn.magicpatterns.com/uploads/c5QoMT1v5QCTRKr5hDj9Rb/image.png',
  imageAlt: 'Smiling café owner serving a customer at the counter beside a Kayko POS terminal',
  enhance: true,
  visual: <SalesMock data-id="element-385" data-mp="31tevqzr5xhg9" />
}, {
  title: 'Stay on top of stock.',
  copy: 'Track products and materials, spot low stock, and plan purchases.',
  cta: 'Explore Inventory',
  href: '/products/inventory',
  bullets: ['Track products and raw materials in real time', 'Get low-stock alerts before you run out', 'Create purchase orders and manage suppliers', 'Move stock between shops, stores, and warehouses'],
  image: 'https://cdn.magicpatterns.com/uploads/kFfEVrUcGmArk3dJ617nLd/image.png',
  imageAlt: 'Shop manager and staff member checking stock levels on a tablet between well-stocked shelves',
  visual: <InventoryMock data-id="element-386" data-mp="2ot8avswntcl4" />
}, {
  title: 'Know where your money goes.',
  copy: 'Record expenses and keep a clear view of your business costs.',
  cta: 'Explore Expenses',
  href: '/products/expenses',
  bullets: ['Record expenses as they happen, with receipt photos', 'Sort spending into clear categories automatically', 'Track supplier bills and upcoming payments', 'Compare costs month to month to find savings'],
  image: 'https://cdn.magicpatterns.com/uploads/wrNADDT5bkybMBifNARSAv/image.png',
  imageAlt: 'Business owner recording expenses on her laptop at a bright office desk',
  imagePosition: '85% 15%',
  visual: <ExpensesMock data-id="element-387" data-mp="3ci1aq0gek4l0" />
}, {
  title: 'See the full picture.',
  copy: 'Turn daily transactions into clear sales, profit, and cash flow reports.',
  cta: 'Explore Reports',
  href: '/products/reports',
  bullets: ['View sales, profit, and cash flow on one dashboard', 'Filter performance by day, product, or location', 'Spot trends early and plan with confidence', 'Export reports for your accountant or tax filings'],
  image: 'https://cdn.magicpatterns.com/uploads/eCmtRhPkFYSihX7PNpzm9m/image.png',
  imageAlt: 'Café owner reviewing performance reports on a tablet while his team works behind the counter',
  imagePosition: '85% 20%',
  visual: <ReportsMock data-id="element-388" data-mp="3mbx2d5jw2tb3" />
}, {
  title: 'Put your records to work.',
  copy: 'Use your business data to check your eligibility for working capital.',
  cta: 'Explore Business Financing',
  href: '/products/business-finance',
  bullets: ['Build a verified record of your business performance', 'Check your eligibility for working capital', 'Share your records with lenders only when you choose', 'Track repayments alongside your daily operations'],
  image: 'https://cdn.magicpatterns.com/uploads/gvhj72eyMsAkj9HQHpNahd/image.png',
  imageAlt: 'Fashion boutique owner smiling as she reviews her business growth on a laptop in her shop',
  imagePosition: '80% 30%',
  visual: <FinanceMock data-id="element-389" data-mp="2vdwveav4l0gn" />
}];
export function FeatureSections() {
  return <section className="w-full bg-white py-20" aria-labelledby="features-heading" data-id="element-390" data-mp="2qld9mqmaaz0r">
      <div className="mx-auto max-w-shell px-6" data-id="element-391" data-mp="2mlml6tk8cuze">
        <h2 id="features-heading" className="display text-center text-[32px] font-semibold text-ink sm:text-[40px]" data-id="element-392" data-mp="2miqkwykwtu7s">
          Run your whole business with Kayko.
        </h2>
        <p className="mx-auto mt-4 max-w-[620px] text-center text-[16px] leading-[1.5] text-ink-muted" data-id="element-393" data-mp="2cp0146zqa7yf">
          From the first sale to your financial reports, keep everything connected.
        </p>

        <div className="mt-16 space-y-16 lg:space-y-6" data-id="element-394" data-mp="23oblj7uorzjf">
          {features.map((feature, index) => {
          const visualFirst = index % 2 === 0;
          return <article key={feature.title} className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-16" data-id="element-395" data-mp="1v3zzgohclp3" data-mpi={index}>
                <div className={`relative ${visualFirst ? 'lg:order-1' : 'lg:order-2'}`} data-id="element-396" data-mp="1oqiim8s6ejoz">
                  <div className="relative aspect-[3/2] overflow-hidden rounded-[16px] bg-canvas-warm lg:aspect-[4/3]" data-id="element-397" data-mp="1thiinmwldhvp">
                    <img src={feature.image} alt={feature.imageAlt} loading="lazy" style={feature.imagePosition ? {
                  objectPosition: feature.imagePosition
                } : undefined} className={`absolute inset-0 h-full w-full object-cover object-right ${feature.enhance ? '[filter:contrast(1.08)_saturate(1.12)_brightness(1.04)]' : ''}`} data-id="element-398" data-mp="1xikk8adze60u" />
                  </div>
                  <div className="relative -mt-24 px-4 sm:absolute sm:bottom-5 sm:left-5 sm:mt-0 sm:w-[min(300px,55%)] sm:px-0 lg:bottom-6 lg:left-6" data-id="element-399" data-mp="2l8ye86g4mey2">
                    {feature.visual}
                  </div>
                </div>

                <div className={`max-w-[440px] ${visualFirst ? 'lg:order-2' : 'lg:order-1 lg:justify-self-end'}`} data-id="element-400" data-mp="m4q5ra881kfb">
                  <h3 className="display text-[26px] font-semibold text-ink sm:text-[30px]" data-id="element-401" data-mp="103pk9p20sixr">
                    {feature.title}
                  </h3>
                  <p className="mt-4 text-[16px] leading-[1.6] text-ink-muted" data-id="element-402" data-mp="7fag5xkffl17">{feature.copy}</p>
                  <ul className="mt-6 space-y-3 border-t border-canvas-line pt-6" data-id="element-403" data-mp="2gqvs1lhehay">
                    {feature.bullets.map(bullet => <li key={bullet} className="flex items-start gap-3 text-[15px] leading-[1.5] text-ink-soft" data-id="element-404" data-mp="h654r8l5awpt">
                        <span className="mt-[3px] flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-brand-soft" data-id="element-405" data-mp="cdc1yq98lygd">
                          <CheckIcon className="h-3 w-3 text-brand" strokeWidth={2.5} aria-hidden="true" data-id="element-406" data-mp="3fvn3oy2ve0hi" />
                        </span>
                        {bullet}
                      </li>)}
                  </ul>
                  <SmartLink href={feature.href} className="mt-7 inline-flex items-center gap-2 text-[15px] font-medium text-brand transition-colors duration-150 ease-out hover:text-brand-hover" data-id="element-407" data-mp="204t9y2a4jh8e">
                    {feature.cta}
                    <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-408" data-mp="2e4vq9k2sa2lj" />
                  </SmartLink>
                </div>
              </article>;
        })}
        </div>
      </div>
    </section>;
}